import { FC, useState, useEffect, useRef } from 'react';
import {
  Modal,
  Text,
  Group,
  Image,
  Stack,
  FileInput,
  Box,
  ActionIcon,
} from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import { useMedplum } from '@medplum/react';
import { createReference } from '@medplum/core';
import { Encounter, Patient, Attachment } from '@medplum/fhirtypes';
import { IconX } from '@tabler/icons-react';
import classes from './EncounterImageUploadModal.module.css';
import { useTranslation } from 'react-i18next';
import FormActionButtons, { FormActionButtonsRef } from '@/components/FormActionButtons';
import { isEqual } from 'lodash';

interface EncounterImageUploadModalProps {
  opened: boolean;
  onClose: () => void;
  encounter: Encounter | null;
  patient?: Patient;
}

const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/heic'];

const EncounterImageUploadModal: FC<EncounterImageUploadModalProps> = ({
  opened,
  onClose,
  encounter,
  patient,
}) => {
  const [files, setFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const medplum = useMedplum();
  const { t } = useTranslation(['common', 'images-tab']);
  const formActionsRef = useRef<FormActionButtonsRef>(null);
  const baselineRef = useRef<File[]>([]);

  useEffect(() => {
    if (opened) {
      baselineRef.current = [];
    } else {
      setFiles([]);
      baselineRef.current = [];
    }
  }, [opened]);

  const handleRemoveFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const hasUnsavedChanges = (): boolean => {
    try {
      return !isEqual(files, baselineRef.current);
    } catch {
      return true;
    }
  };

  const handleClose = () => {
    setFiles([]);
    onClose();
  };

  const handleDiscardChanges = () => {
    setFiles(baselineRef.current);
  };

  const handleUpload = async () => {
    if (!encounter || !patient) {
      return;
    }
    setUploading(true);

    try {
      await Promise.all(
        files.map(async (file) => {
          const attachment: Attachment = await medplum.createAttachment({
            data: file,
            contentType: file.type || 'application/octet-stream',
            filename: file.name,
          });

          await medplum.createResource({
            resourceType: 'Media',
            status: 'completed',
            type: { text: 'Image' },
            content: attachment,
            encounter: createReference(encounter),
            subject: createReference(patient),
            createdDateTime: new Date().toISOString(),
          });
        })
      );

      showNotification({
        color: 'green',
        title: t('success', { ns: 'common' }),
        message: t('uploadSuccess', { ns: 'images-tab' }),
      });

      setFiles([]);
      onClose();
    } catch (err) {
      showNotification({
        color: 'red',
        title: t('uploadFailed', { ns: 'images-tab' }),
        message: err instanceof Error ? err.message : String(err),
      });
    } finally {
      setUploading(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={() => formActionsRef.current?.handleGuardedClose()}
      title={t('addImages', { ns: 'images-tab' })}
      size="lg"
      closeOnClickOutside={false}
      closeOnEscape={false}
    >
      {encounter ? (
        <Stack>
          <FileInput
            accept={ACCEPTED_IMAGE_TYPES.join(',')}
            multiple
            label={t('selectImages', { ns: 'images-tab' })}
            placeholder={t('chooseFiles', { ns: 'images-tab' })}
            onChange={(selected) => {
              if (!selected) {
                setFiles([]);
                return;
              }

              setFiles(Array.isArray(selected) ? selected : [selected]);
            }}
            value={files}
            clearable
            disabled={uploading}
          />

          {files.length > 0 && (
            <Group>
              {files.map((file, idx) => (
                <Box key={idx} pos="relative">
                  <Image
                    src={URL.createObjectURL(file)}
                    width={80}
                    height={80}
                    radius="md"
                    alt={file.name}
                    fit="cover"
                  />
                  <ActionIcon
                    variant="filled"
                    radius="xl"
                    size="sm"
                    onClick={() => handleRemoveFile(idx)}
                    disabled={uploading}
                    className={classes.removeIcon}
                  >
                    <IconX size={14} />
                  </ActionIcon>
                </Box>
              ))}
            </Group>
          )}

          <FormActionButtons
            ref={formActionsRef}
            onSave={handleUpload}
            onDiscard={handleDiscardChanges}
            onClose={handleClose}
            hasUnsavedChanges={hasUnsavedChanges()}
            isLoading={uploading}
            isFormValid={files.length > 0}
            saveLabel={t('save', { ns: 'common' })}
          />
        </Stack>
      ) : (
        <Text>{t('noEncounterSelected', { ns: 'images-tab' })}</Text>
      )}
    </Modal>
  );
};

export default EncounterImageUploadModal;
